/**
 * AliasCardDialog — printable/shareable "business card" view for an alias.
 *
 * Layout:
 *   Top:     centered title (display_name | alias_name | "Contact")
 *            centered smaller email line
 *   Middle:  LEFT  → 4 rows × 4 chars of the alias local-part, row-numbered,
 *                    monospace, large, wide-spaced
 *            RIGHT → centered QIB logo above a QR code of the full email
 *   Bottom:  centered @quickinbox.app + small disclaimer
 *
 * Props:
 *   open          : boolean
 *   onOpenChange  : (v: boolean) => void
 *   alias         : { alias_address, label, display_name?, alias_name? }
 *
 * Only renders a dialog when alias is truthy. Safe no-op otherwise.
 */
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** Group the alias local-part into rows based on length:
 *    • ≤6 chars  → single row, no splitting
 *    • 7–12 chars → two balanced rows (ceil(n/2) | rest)
 *    • ≥13 chars  → 4-character grouping (last row may be short)
 *  Zero padding. Zero truncation. Characters preserved exactly. */
function buildCharGrid(localPart = "") {
  const s = String(localPart || "");
  if (!s) return [];
  if (s.length <= 6) return [s];
  if (s.length <= 12) {
    const mid = Math.ceil(s.length / 2);
    return [s.slice(0, mid), s.slice(mid)];
  }
  const rows = [];
  for (let i = 0; i < s.length; i += 4) rows.push(s.slice(i, i + 4));
  return rows;
}

/** Split a display name into words. Rule: split ONLY on whitespace. Hyphenated
 *  words stay joined ("Kamp-Karrenbauer" is one word). Each word is rendered on
 *  its own line to guarantee the name never feels broken or artificially split.
 *  If a single word is wider than the container, CSS `overflow-wrap: anywhere`
 *  combined with `clamp()` font-scaling handles the edge case gracefully —
 *  the browser will only break the word as a last resort after the font has
 *  shrunk to its minimum. Automatic hyphenation is disabled. */
function splitTitleWords(title) {
  return String(title || "").trim().split(/\s+/).filter(Boolean);
}

function resolveTitle(alias) {
  if (!alias) return "Contact";
  return (
    (alias.display_name && String(alias.display_name).trim()) ||
    (alias.alias_name && String(alias.alias_name).trim()) ||
    (alias.label && String(alias.label).trim()) ||
    "Contact"
  );
}

export default function AliasCardDialog({ open, onOpenChange, alias }) {
  if (!alias) return null;

  const email = alias.alias_address || "";
  const localPart = email.split("@")[0] || "";
  const domain = email.split("@")[1] || "quickinbox.app";
  const title = resolveTitle(alias);
  const rows = buildCharGrid(localPart);
  const titleWords = splitTitleWords(title);
  // Font-size clamp: 16px → 28px desktop, scales down to 12px on very long words.
  // Reduces BEFORE hitting overflow-wrap breakpoints (per UX spec).
  const titleStyle = {
    fontSize: `clamp(12px, ${Math.max(20 - Math.max(0, title.length - 14), 12)}px, 28px)`,
    overflowWrap: "anywhere",
    hyphens: "manual",
    wordBreak: "normal",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden"
        data-testid="alias-card-dialog"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title} — alias card</DialogTitle>
        </DialogHeader>

        {/* Top: title + email (centered) */}
        <div className="px-6 pt-6 pb-4 text-center border-b border-[#E5E5E5]">
          <h2
            className="font-semibold text-[#111111] leading-[1.15]"
            style={titleStyle}
            data-testid="alias-card-title"
          >
            {titleWords.length === 0 ? (
              <span>Contact</span>
            ) : (
              titleWords.map((w, i) => (
                <span
                  key={i}
                  className="block"
                  data-testid={`alias-card-title-word-${i + 1}`}
                >
                  {w}
                </span>
              ))
            )}
          </h2>
          <p
            className="mt-2 text-xs text-[#555555] font-mono break-all"
            data-testid="alias-card-email"
          >
            {email}
          </p>
        </div>

        {/* Middle: char grid | QR (QR top-aligned to first row) */}
        <div className="px-6 py-6 grid grid-cols-[auto_1fr] sm:grid-cols-2 gap-6 items-start">
          {/* LEFT — adaptive char grid */}
          <div data-testid="alias-card-grid" aria-label="Alias code grid">
            {rows.map((row, idx) => {
              // Tracking adapts to row density: short rows breathe more.
              const tracking =
                rows.length === 1
                  ? "tracking-[0.18em]"
                  : row.length <= 4
                    ? "tracking-[0.35em]"
                    : row.length <= 6
                      ? "tracking-[0.22em]"
                      : "tracking-[0.14em]";
              return (
                <div
                  key={idx}
                  className="flex items-baseline gap-3 leading-[1.8]"
                  data-testid={`alias-card-grid-row-${idx + 1}`}
                >
                  {rows.length > 1 && (
                    <span className="text-[11px] text-[#888888] font-mono w-4 text-right select-none">
                      {idx + 1}
                    </span>
                  )}
                  <span className={`font-mono text-xl sm:text-2xl text-[#111111] ${tracking}`}>
                    {row}
                  </span>
                </div>
              );
            })}
          </div>

          {/* RIGHT — logo + QR */}
          <div className="flex flex-col items-center gap-3">
            <img
              src="/brand/qib-logo-128.png"
              alt="QuickInbox"
              className="w-14 h-14 rounded-lg object-contain"
              data-testid="alias-card-logo"
            />
            <div
              className="p-2 bg-white rounded-md border border-[#E5E5E5]"
              data-testid="alias-card-qr-wrap"
            >
              <QRCodeSVG
                value={email.replace(/\s+/g, "")}
                size={120}
                level="M"
                marginSize={0}
                data-testid="alias-card-qr"
                aria-label={`QR code for ${email}`}
              />
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="px-6 pb-6 pt-2 text-center border-t border-[#E5E5E5]">
          <p className="text-sm font-medium text-[#111111]" data-testid="alias-card-domain">
            @{domain}
          </p>
          <p className="mt-1 text-[10px] text-[#888888] leading-snug">
            Anonymous relay address. Anyone with this card can reach this inbox.
            Revoke the alias from QuickInbox if it is ever misused.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
